import React, { useState, useRef } from 'react';
import classnames from 'classnames';
import { Input, Form } from 'antd';
import { useSelector } from 'react-redux';

import * as queries from '~settings/pms-queries';
import { propertyStatus } from '~constants/listing-management';
import { formatPayload } from '~helpers/withdrawal';
import { listingManagementFetch } from '~helpers/graphql';
import { PaymentMethodEnum, methodType } from './constant';

import PropertyDetailWrapper from '~components/property-detail-wrapper';
import BankTransfer from '~pages/withdrawal/edit/bank-transfer';
import RadioCard from './components/radio-card';
import PaymentLink from './components/payment-link';
import OthersPayment from './components/others-payment';
import VirtualCreditCardMethod from './components/virtual-credit-card';

const PaymentMethod = ({ t, property, afterSave, form }) => {
  const { landlordCountry } = useSelector(state => ({
    landlordCountry: state.auth.getIn(['base', 'partnerCountry']),
  }));
  const [inputError, setInputError] = useState(false);
  const [instructionValue, setInstructionValue] = useState('');
  const [instructionValueCount, setInstructionValueCount] = useState(0);
  const [selectedMethodType, setSelectedMethodType] = useState(
    property?.offlinePaymentMethod?.methodType ?? methodType.PAYMENT_LINK,
  );
  const [queryParams, setQueryParams] = useState(property?.offlinePaymentMethod);
  const bankTransferRef = useRef();
  const otherPaymentMethodRef = useRef();
  const paymentLinkPaymentMethodRef = useRef();
  const vccPaymentMethodRef = useRef();

  const handleValidateBankTransfer = () => {
    let bankTransferData = {};
    form.validateFields((bankErr, bankValues) => {
      if (!bankErr) {
        bankTransferData = {
          isValid: true,
          data: formatPayload(bankValues),
        };
      } else {
        bankTransferData = {
          isValid: false,
        };
      }
    });
    return bankTransferData;
  };

  const createPaymentMethod = variables => {
    return queryParams?.id
      ? queries.updateOfflinePaymentMethod({ input: variables })
      : queries.createOfflinePaymentMethod({ input: { ...variables, propertyId: property.id } });
  };

  const handleSubmit = () => {
    return new Promise((resolve, reject) => {
      let variables = { ...queryParams, instruction: instructionValue };
      let isValid = false;
      switch (selectedMethodType) {
        case methodType.PAYMENT_LINK:
          isValid = paymentLinkPaymentMethodRef.current(queryParams.data?.paymentLink);
          break;
        case methodType.OTHER:
          isValid = otherPaymentMethodRef.current(queryParams.data?.methodName);
          break;
        case methodType.VIRTUAL_CREDIT_CARD:
          isValid = vccPaymentMethodRef.current(queryParams.data?.email);
          break;
        case methodType.BANK_TRANSFER:
          variables = Object.assign(variables, handleValidateBankTransfer());
          break;
      }

      if ((variables?.isValid || isValid) && !inputError) {
        delete variables.isValid;
        let isUpdate = false;

        listingManagementFetch(createPaymentMethod(variables))
          .then(res => {
            if (queryParams?.id && res.updateOfflinePaymentMethod?.offlinePaymentMethod?.id) {
              isUpdate = true;
              afterSave({
                ...property,
                publishCondition: {
                  ...property.publishCondition,
                  offlinePaymentMethod: true,
                },
                offlinePaymentMethod: variables,
              });
            } else if (res.createOfflinePaymentMethod?.offlinePaymentMethod?.id) {
              isUpdate = true;
              const updatePropertyData = {
                ...property,
                publishCondition: {
                  ...property.publishCondition,
                  offlinePaymentMethod: true,
                },
                offlinePaymentMethod: {
                  ...variables,
                  id: res.createOfflinePaymentMethod?.offlinePaymentMethod?.id,
                },
              };
              afterSave(updatePropertyData);
            }
          })
          .catch(e => {
            reject(e);
          });

        if (isUpdate) {
          resolve({ isUpdated: true });
        }
      } else {
        resolve({ isUpdated: false });
      }
    });
  };

  const handleInstructionsInputChange = event => {
    const inputValue = event.target.value;
    setInstructionValueCount(inputValue.length);
    setInputError(inputValue.length > 1000);
    if (inputValue.length <= 1000) {
      setInstructionValue(inputValue);
    }
  };

  const handleSelectedMethodType = methodData => {
    setQueryParams({ ...queryParams, methodType: methodData.id });
    setSelectedMethodType(methodData.id);
  };

  return (
    <PropertyDetailWrapper
      t={t}
      isPublished={property.status === propertyStatus.PUBLISHED}
      property={property}
      onClickSave={handleSubmit}
    >
      <div className="payment-method">
        <div className="payment-methods-wrapper">
          <div className="payment-method__section--title">
            {t('cms:dashboard.property.property_management.payment_method.payment_methods')}
          </div>
          <div className="choose-method">Choose method:</div>
          <div>
            {PaymentMethodEnum.map((method, index) => (
              <div
                className="payment-method-type-wrapper"
                onClick={() => handleSelectedMethodType(method)}
                key={index}
              >
                <RadioCard
                  methodName={method.method}
                  icon={method.icon}
                  isChecked={selectedMethodType === method.id}
                >
                  <div className="method-type-wrapper">
                    <Choose>
                      <When condition={selectedMethodType === methodType.PAYMENT_LINK}>
                        <PaymentLink
                          t={t}
                          queryParams={queryParams}
                          setQueryParams={setQueryParams}
                          ref={paymentLinkPaymentMethodRef}
                        />
                      </When>
                      <When condition={selectedMethodType === methodType.BANK_TRANSFER}>
                        <BankTransfer
                          desc={t(
                            'properties.listing_management.payment_method.bank_transfer.desc',
                          )}
                          t={t}
                          data={queryParams?.data ?? {}}
                          country={landlordCountry}
                          form={form}
                          ref={form => (bankTransferRef.current = form)}
                        />
                      </When>
                      <When condition={selectedMethodType === methodType.VIRTUAL_CREDIT_CARD}>
                        <VirtualCreditCardMethod
                          t={t}
                          queryParams={queryParams}
                          setQueryParams={setQueryParams}
                          ref={vccPaymentMethodRef}
                        />
                      </When>
                      <When condition={selectedMethodType === methodType.OTHER}>
                        <OthersPayment
                          t={t}
                          queryParams={queryParams}
                          setQueryParams={setQueryParams}
                          ref={otherPaymentMethodRef}
                        />
                      </When>
                    </Choose>
                  </div>
                </RadioCard>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="payment-method__section--title instructions-title">
            {t('cms:dashboard.property.property_management.payment_method.instructions')}
          </div>
          <div className="instructions-desc">
            {t('cms:dashboard.property.property_management.payment_method.instructions.desc')}
          </div>
          <div className="instructions-textarea-wrapper">
            <div className="instructions-label">
              {t('cms:dashboard.property.property_management.payment_method.instructions')}:
            </div>
            <Input.TextArea
              placeholder={t(
                'cms:dashboard.property.property_management.payment_method.instructions.input.placeholder',
              )}
              className={classnames(inputError && 'border-error')}
              onChange={handleInstructionsInputChange}
              rows={4}
              defaultValue={property?.offlinePaymentMethod?.instruction}
            />
            <div
              className={classnames(
                'instructions-error-wrapper',
                inputError && 'instructions-error',
              )}
              style={{ justifyContent: inputError ? 'space-between' : 'flex-end' }}
            >
              <If condition={inputError}>
                <div>
                  {t(
                    'cms:dashboard.property.property_management.payment_method.instructions.input.error',
                  )}
                </div>
              </If>
              <div className={classnames('instructions-count', inputError && 'error')}>
                {instructionValueCount}/1000
              </div>
            </div>
          </div>
        </div>
      </div>
    </PropertyDetailWrapper>
  );
};

export default Form.create({ name: 'paymentMethod' })(PaymentMethod);
